import { Box, Chip, Tooltip, IconButton, Typography, Stack, Badge } from '@mui/material';
import { alpha } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ControlCameraIcon from '@mui/icons-material/ControlCamera';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GroupIcon from '@mui/icons-material/Group';
import type { MultiUserSessionState, SessionRole } from '@/types/webrtc';

interface SessionRoleBarProps {
  role: SessionRole;
  sessionState: MultiUserSessionState | null;
  onTakeover: () => void;
  onLock: () => void;
  onUnlock: () => void;
}

export const SessionRoleBar: React.FC<SessionRoleBarProps> = ({
  role,
  sessionState,
  onTakeover,
  onLock,
  onUnlock,
}) => {
  if (!sessionState && role === 'pending') return null;

  const isController = role === 'controller';
  const isObserver = role === 'observer';
  const locked = sessionState?.locked ?? false;
  const participantCount = sessionState?.participants.length ?? 0;
  const controllerName = sessionState?.controllerName ?? '';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        bgcolor: (theme) =>
          isController
            ? alpha(theme.palette.success.main, 0.12)
            : alpha(theme.palette.warning.main, 0.12),
        border: '1px solid',
        borderColor: (theme) =>
          isController
            ? alpha(theme.palette.success.main, 0.3)
            : alpha(theme.palette.warning.main, 0.3),
      }}
    >
      {/* Role chip */}
      {isController ? (
        <Chip
          icon={<ControlCameraIcon fontSize="small" />}
          label="Controller"
          size="small"
          color="success"
          variant="filled"
        />
      ) : (
        <Chip
          icon={<VisibilityIcon fontSize="small" />}
          label="Observer"
          size="small"
          color="warning"
          variant="filled"
        />
      )}

      {/* Separator */}
      <Box sx={{ width: 1, bgcolor: 'divider', alignSelf: 'stretch' }} />

      {/* Controller info (for observers) */}
      {isObserver && controllerName && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {controllerName} controla
        </Typography>
      )}

      {/* Participants count */}
      {participantCount > 1 && (
        <Tooltip
          title={
            <Stack spacing={0.5}>
              {sessionState?.participants.map((p) => (
                <Box key={p.sessionId} sx={{ display: 'flex', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: p.role === 'controller' ? 700 : 400 }}
                  >
                    {p.username}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    ({p.role})
                  </Typography>
                </Box>
              ))}
            </Stack>
          }
        >
          <Badge badgeContent={participantCount} color="primary">
            <GroupIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </Badge>
        </Tooltip>
      )}

      {/* Lock indicator */}
      {locked && (
        <Tooltip title="Sesión bloqueada — el controller tiene control exclusivo">
          <LockIcon fontSize="small" color="error" />
        </Tooltip>
      )}

      {/* Actions */}
      <Stack direction="row" spacing={0.5}>
        {isObserver && !locked && (
          <Tooltip title="Tomar control">
            <IconButton size="small" color="warning" onClick={onTakeover}>
              <ControlCameraIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {isController && !locked && (
          <Tooltip title="Bloquear control (impide takeover)">
            <IconButton size="small" color="default" onClick={onLock}>
              <LockIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {isController && locked && (
          <Tooltip title="Desbloquear control">
            <IconButton size="small" color="error" onClick={onUnlock}>
              <LockOpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};
